import React, { ForwardedRef, forwardRef, ReactNode, useImperativeHandle, useRef, useState } from 'react';
import UXTextInput from '@ux/text-input';

import CTAaddOnWrapper from './CTAaddOnWrapper';
import '@ux/text-input/styles';
import useRemoveDisabledAddReadOnlyAttrs from './hooks/useRemoveDisabledAddReadOnlyAttrs';
import ShowHideButton from './ShowHideButton/ShowHideButton';
import Box from './Box/Box';

interface InputChangeEvent {
  event: React.ChangeEvent<HTMLInputElement>;
}

export type ConditionalProps =
  | {
      type?: 'text' | 'email' | 'url' | 'tel' | 'number' | 'search';
    }
  | {
      type?: 'password';
      showButtonText: string;
      hideButtonText: string;
    };

export type Props = ConditionalProps & {
  className?: string;
  defaultValue?: string;
  disabled?: boolean;
  id?: string;
  invalid?: boolean;
  label?: ReactNode | string;
  multiline?: boolean;
  onBlur?: (_event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (value: string, eventObj: InputChangeEvent) => void;
  onFocus?: (_event: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  resize?: boolean | 'horizontal' | 'vertical';
  required?: boolean;
  size?: number;
  suggestion?: string;
  value?: ReactNode;
  helpMessage?: string;
  errorMessage?: ReactNode;
  autoComplete?: string;
  labelExperimentalFloat?: boolean;
  inputMode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';
  focus?: boolean;
  ctaAddOn?: ReactNode | undefined;
};

const TextInput = ({ ctaAddOn, ...props }: Props, inputRef: ForwardedRef<HTMLInputElement>) => {
  let { type } = props;
  let showButtonText = '';
  let hideButtonText = '';
  let filteredProps: Omit<Props, 'showButtonText' | 'hideButtonText' | 'type' | 'label'> = props;

  if (type === 'password') {
    ({ showButtonText, hideButtonText, type, ...filteredProps } = props as Extract<Props, { type: 'password' }>);
  }

  const [inputType, setType] = useState(type);
  const ref = useRef<HTMLInputElement>(null);

  // sync forwarded ref with the inputRef ref
  useImperativeHandle(inputRef, () => ref.current as HTMLInputElement);

  useRemoveDisabledAddReadOnlyAttrs(props.disabled, ref);

  const toggle = () => {
    setType(currentType => (currentType === 'password' ? 'text' : 'password'));
  };

  return (
    <Box position='relative'>
      <CTAaddOnWrapper isFloatingLabel={props.labelExperimentalFloat} ctaAddon={ctaAddOn}>
        <UXTextInput type={inputType} {...filteredProps} ref={ref} />
        {type === 'password' && (
          <ShowHideButton handleClick={toggle} text={inputType === 'password' ? showButtonText : hideButtonText} />
        )}
      </CTAaddOnWrapper>
    </Box>
  );
};

export default forwardRef(TextInput);
